import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useDrawerEscape } from '../../../lib/hooks/useDrawerEscape';

interface NotionDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  /** Header personalizzato — quando passato, sostituisce title/subtitle/X di default */
  header?: React.ReactNode;
  children: React.ReactNode;
  width?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'wide';
  footer?: React.ReactNode;
  /** Se true, disabilita il padding default sul body (px-6 py-4). Utile per tabelle full-width o toolbar sticky. */
  noBodyPadding?: boolean;
  /** Opt-in for a CRM drawer sharing space with the Flù workspace. */
  workspace?: 'user';
}

const widthClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  // Tabelle con molte colonne: quasi tutto lo schermo.
  wide: 'max-w-[92vw]'
};

export function NotionDrawer({
  isOpen,
  onClose,
  title,
  subtitle,
  header,
  children,
  width = 'md',
  footer,
  noBodyPadding = false,
  workspace
}: NotionDrawerProps) {
  // Escape condiviso: chiude solo il pannello piu' in alto (layer = z-index)
  useDrawerEscape(isOpen, onClose, 10000);

  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div data-flu-workspace={workspace==='user'?'user-drawer':undefined} className="fixed inset-0 z-[10000] flex justify-end">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Drawer Panel */}
      <div
        className={`
          relative w-full ${widthClasses[width]}
          bg-white dark:bg-gray-900
          shadow-xl
          flex flex-col
          animate-in slide-in-from-right duration-300
        `}
      >
        {/* Header */}
        {header ? (
          <div className="px-6 py-4 border-b border-[rgba(55,53,47,0.09)] dark:border-gray-700">
            {header}
          </div>
        ) : (title || subtitle) ? (
          <div className="flex items-start justify-between px-6 py-4 border-b border-[rgba(55,53,47,0.09)] dark:border-gray-700">
            <div>
              {title && (
                <h2 className="text-lg font-semibold text-[#37352F] dark:text-white">
                  {title}
                </h2>
              )}
              {subtitle && (
                <p className="text-sm text-[#787774] dark:text-gray-400 mt-1">
                  {subtitle}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="
                p-1.5 rounded-md
                text-[#787774] dark:text-gray-400
                hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-700
                transition-colors
              "
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        ) : null}

        {/* Content */}
        <div className={`flex-1 overflow-y-auto ${noBodyPadding ? '' : 'px-6 py-4'}`}>
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="px-6 py-4 border-t border-[rgba(55,53,47,0.09)] dark:border-gray-700 bg-[#F7F7F5] dark:bg-gray-800">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

export default NotionDrawer;
